import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio, ArrowRight } from 'lucide-react';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Card, CardHeader, CardTitle, CardBody, Button, Badge } from '@vnetwork/ui/primitives';
import { useAesHealth, useDrmStreams, useDrmUsageSummary, type DrmStream } from '@/api/aes';
import { streamStatusTone, STREAM_STATUS_LABEL, kindTone, KIND_LABEL } from './shared';
import { DrmAnalyticsSection } from './DrmAnalyticsSection';
import styles from './Drm.module.css';

/**
 * Self-hosted streams overview BODY — data-plane health strip + analytics + recent
 * streams, WITHOUT a page header. It is embedded in the unified AES gateway
 * Overview (the merged single overview), so the page header comes from the parent.
 * `base` is the DRM route base (`/services/aes/:id/drm`) for the stream links.
 */
export function SelfHostedOverview({ serviceId, base }: { serviceId?: string; base: string }) {
  const navigate = useNavigate();
  const health = useAesHealth(serviceId); // sig-drm key server (via @mgmt)
  const streamsQ = useDrmStreams(); // sig-control (direct, JWT — tenant implicit)
  const usageQ = useDrmUsageSummary();

  const streams = useMemo(() => streamsQ.data ?? [], [streamsQ.data]);

  // sig-packager: derive shard health from live streams that have a shard assigned.
  // shard 0 is a VALID assignment (PACKAGER_SHARDS=1), so test for presence, not
  // truthiness — 0 is falsy in JS.
  const liveStreams = streams.filter((s) => s.status === 'live');
  const sharded = liveStreams.filter((s) => s.shard != null).length;
  const packagerOk = liveStreams.length === 0 || sharded === liveStreams.length;

  const usageRows = usageQ.data?.rows ?? [];

  const recent = useMemo(
    () => [...streams].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')).slice(0, 5),
    [streams],
  );

  const columns = useMemo<ColumnDef<DrmStream, unknown>[]>(
    () => [
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={streamStatusTone(row.original.status)}>{STREAM_STATUS_LABEL[row.original.status]}</StatusPill>
        ),
      },
      {
        id: 'kind',
        header: 'Kind',
        cell: ({ row }) => (
          <Badge tone={kindTone(row.original.kind)} appearance="subtle" size="sm">
            {KIND_LABEL[row.original.kind]}
          </Badge>
        ),
      },
      { id: 'stream', header: 'Stream', cell: ({ row }) => <span>{row.original.id}</span> },
      {
        id: 'shard',
        header: 'Shard',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{row.original.shard ?? '—'}</span>
        ),
      },
    ],
    [],
  );

  // Role-based health labels — internal codenames never surface in the UI.
  const drmTone = health.data?.ok ? 'success' : health.isLoading ? 'muted' : 'danger';
  const controlTone = streamsQ.isSuccess ? 'success' : streamsQ.isLoading ? 'muted' : 'danger';
  const packagerTone = streamsQ.isError ? 'muted' : packagerOk ? 'success' : 'warning';
  const planeWord = (t: string) => (t === 'success' ? 'Online' : t === 'muted' ? 'Checking' : t === 'warning' ? 'Degraded' : 'Unreachable');

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Data-plane health strip. */}
      <Card flat>
        <CardBody>
          <div className={styles.planeStrip}>
            <StatusPill tone={controlTone} loading={streamsQ.isLoading}>Control {planeWord(controlTone)}</StatusPill>
            <StatusPill tone={drmTone} loading={health.isLoading}>Key server {planeWord(drmTone)}</StatusPill>
            <StatusPill tone={packagerTone}>Packager {streamsQ.isError ? '—' : `${sharded}/${liveStreams.length} shards`}</StatusPill>
            <span className={styles.planeMeta}>HLS AES-128 · live key rotation</span>
          </div>
        </CardBody>
      </Card>

      <DrmAnalyticsSection serviceId={serviceId} base={base} streams={streams} usageRows={usageRows} />

      {/* Recent streams — full-width table; manage links live in the sidebar nav. */}
      <Card flat>
        <CardHeader
          actions={
            <Button size="sm" variant="ghost" endIcon={<ArrowRight size={14} />} onClick={() => navigate(`${base}/streams`)}>
              View all
            </Button>
          }
        >
          <CardTitle className={styles.cardTitle}>Recent streams</CardTitle>
        </CardHeader>
        <CardBody>
          <DataTable
            columns={columns}
            data={recent}
            getRowId={(s) => s.id}
            dense
            onRowClick={(s) => navigate(`${base}/streams/${encodeURIComponent(s.id)}`)}
            emptyState={
              <EmptyState
                icon={<Radio size={20} />}
                title="No streams yet"
                description="Provision a source to see it here."
                actions={
                  <Button size="sm" onClick={() => navigate(`${base}/streams/new`)}>
                    Add source
                  </Button>
                }
              />
            }
          />
        </CardBody>
      </Card>
    </div>
  );
}

export default SelfHostedOverview;
